import { Alert } from "@mui/material";

import { useAnalytics } from "@/providers/analytics";
import { AutocompleteType } from "@/shared/components/TagAndMetadataAutocomplete";
import { TagInput, Tags, TagsGroup } from "@/shared/components/tags";
import { type SessionRecord } from "@/shared/domain/sessions";
import { useUpdateSession } from "@/shared/domain/sessions/hooks";
import { userFacingErrorMessage } from "@/shared/http";

import styles from "./SessionTags.module.css";

interface SessionTagsProps {
  session: SessionRecord;
}

export function SessionTags({ session }: SessionTagsProps) {
  const { trackEvent } = useAnalytics();
  const { mutateAsync: updateSession, error, isPending } = useUpdateSession();

  async function handleAddTag(tag: string) {
    trackEvent("AddSessionTagClicked", { sessionId: session.session_id });
    try {
      await updateSession({
        sessionId: session.session_id,
        updates: { metadata_changeset: { put_tags: [tag] } },
      });
    } catch {
      // Swallowed on purpose: useMutation exposes the failure as `error`,
      // which MutationErrorAlert renders below.
    }
  }

  async function handleRemoveTag(tag: string) {
    trackEvent("RemoveSessionTagClicked", { sessionId: session.session_id });
    try {
      await updateSession({
        sessionId: session.session_id,
        updates: { metadata_changeset: { remove_tags: [tag] } },
      });
    } catch {
      // Swallowed on purpose: useMutation exposes the failure as `error`,
      // which MutationErrorAlert renders below.
    }
  }

  return (
    <div className={styles.container}>
      <ExistingTags tags={session.tags} onRemove={handleRemoveTag} />
      <TagInput
        autocompleteType={AutocompleteType.SessionTags}
        onAddTag={(tag) => void handleAddTag(tag)}
        tags={session.tags}
        size="small"
        disabled={isPending}
        isLoading={isPending}
      />
      <MutationErrorAlert error={error} />
    </div>
  );
}

function ExistingTags({
  tags,
  onRemove,
}: {
  tags: string[];
  onRemove: (tag: string) => Promise<void>;
}) {
  if (tags.length === 0) {
    return null;
  }
  return (
    <TagsGroup>
      <Tags tags={tags} onDeleteTag={(tag) => void onRemove(tag)} />
    </TagsGroup>
  );
}

function MutationErrorAlert({ error }: { error: Error | null }) {
  if (!error) {
    return null;
  }
  return (
    <Alert severity="error" className={styles.inlineError}>
      {userFacingErrorMessage(error, "Failed to update session tags.")}
    </Alert>
  );
}
